/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      
      // Exit early if section is not found (e.g., during DOM manipulation in headless browsers)
      if (!this.section) {
        return;
      }
      
      this.productForm = this.section.querySelector(".js-product-form");
      this.optionSelectors = this.querySelectorAll(
        ".option-selector:not(.option-selector--custom)",
      );
      this.nativeSelector = document.getElementById(
        `variants-${this.section.dataset.section}`,
      );
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.checkAndHideSelectors();
      this.updateAvailability();
      this.updateAddToCartButton();
      this.addEventListener("change", this.handleVariantChange.bind(this));
      this.applySearchParams();

      this.setAttribute("loaded", "");
    }

    hideSelector(selector) {
      if (selector) {
        selector.style.display = "none";
      }
    }

    /**
     * Checks if the product is a hardline product and hides selectors if only one option is available.
     */
    checkAndHideSelectors() {
      const isHardlineProduct =
        window.cultstore?.productData?.isHardlineProduct;
      // Ensure this logic only runs for hardline products
      if (isHardlineProduct !== "hardline") return;

      // Check and hide color options
      const colorFieldset = this.querySelector('fieldset[data-option="Color"]');
      if (colorFieldset) {
        const colorLabels = Array.from(
          colorFieldset.querySelectorAll(".swatch-product__text, .js-value"),
        ).filter((label) => !label.classList.contains("visually-hidden")); // Ignore visually-hidden elements

        // Count the total number of visible items
        const totalItems = colorLabels.length;

        if (totalItems <= 1) {
          this.hideSelector(colorFieldset); // Hide if 1 or fewer visible items
        } else {
          colorFieldset.style.display = ""; // Ensure it's visible if more than 1 visible item
        }
      }

      // Check and hide size options
      const sizeSelector = this.querySelector(
        '.option-selector[data-option="Size"]',
      );
      if (sizeSelector) {
        const sizeOptions = sizeSelector.querySelectorAll(".js-option");
        if (sizeOptions.length === 1) {
          this.hideSelector(sizeSelector);
        }
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      //set selected variant in currentProductData
      window.customProductData.selectedVariant = this.getSelectedVariant();

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
          },
        }),
      );
    }

    /**
     * Updates the "Add to Cart" button label and status.
     */
    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant
        ? theme.strings.noStock
        : theme.strings.noVariant;
      if (this.addBtn) {
        this.addBtn.disabled = !variantAvailable;
        // Prevent clicks on mobile when disabled
        this.addBtn.style.pointerEvents = variantAvailable ? '' : 'none';
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === "selection") {
        // Flag all options as unavailable
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available,
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])',
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable,
            );
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product",
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (
        this.variant &&
        this.variant.available &&
        typeof this.pickUpAvailability.getAvailability === "function"
      ) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price || this.section.querySelector(".product-price .price");
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price)
            variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML =
            this.data.formatted[variant.id].compareAtPrice
              ? `<span class="price__was-label">MRP:</span> <s class="price__was-value">${this.data.formatted[variant.id].compareAtPrice}</s>`
              : "";

        // Update unit price, if specified.
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle(
          "price--on-sale",
          variant.compare_at_price > variant.price,
        );
        this.price.classList.toggle(
          "price--sold-out",
          !variant.available && !this.preSelection,
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      this.price.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;
      const from = this.price.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode =
        this.barcode || this.section.querySelector(".product-barcode__value");
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
      return;

      const url = new URL(this.dataset.url, window.location.origin);
      const searchParams = new URLSearchParams(window.location.search);

      if (this.variant) {
      searchParams.set("variant", this.variant.id);
      } else {
      searchParams.delete("variant");
      }

      url.search = searchParams.toString();
      window.history.replaceState({}, "", url.toString());
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(".js-product-form, .js-instalments-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : "";
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]',
          );
          selectedOptions.push(
            selected && selected.dataset.value !== ""
              ? selected.dataset.value
              : null,
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find((v) =>
        v.options.every((val, index) => val === this.selectedOptions[index]),
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find(
          (x) => x.dataset.option === key,
        );
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === "dropdown") {
          const colorOptionDropdown = matchingOptionSelector.querySelector(
            `.custom-select__option[data-value="${value}"]`,
          );
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest("custom-select");
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(
            `input[value="${value}"]`,
          );
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector(
              ".option-selector__label-value",
            );
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector(
                ".option-selector__label-value",
              ).textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent("change", { bubbles: true, cancelable: false }),
            );
          }
        }
      });
    }
  }

  customElements.define("variant-picker", VariantPicker);
}

/*/ Add size validation to variant picker/*/

document.addEventListener("DOMContentLoaded", function () {
  const sizeSelectors = document.querySelectorAll(
    '.option-selector[data-option="Size"] input[type="radio"]',
  );

  const thicknessSelectors = document.querySelectorAll(
    '.option-selector[data-option="Thickness"] input[type="radio"]',
  );

  const addToCartButtons = document.querySelectorAll(
    'button[name="add"], button[data-button-id="sticky-atc-btn"]',
  ); // Adjust the selector to match your "Add to Cart" buttons

  function isSizeSelected() {
    let isSizeSelected =
      Array.from(sizeSelectors).length > 0
        ? Array.from(sizeSelectors).some((selector) => selector.checked)
        : true;
    let isThicknessSelected =
      Array.from(thicknessSelectors).length > 0
        ? Array.from(thicknessSelectors).some((selector) => selector.checked)
        : true;
    return isSizeSelected && isThicknessSelected;
  }

  function selectSingleOptionIfAvailable(selectors) {
    const availableOptions = Array.from(selectors).filter(
      (selector) => !selector.disabled, // Ensure the option is not disabled
    );

    if (availableOptions.length === 1) {
      const singleOption = availableOptions[0];
      singleOption.checked = true;
      singleOption.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function displaySizeErrorMessage() {
    const messageElement = document.querySelector(".size-error-message");
    if (!isSizeSelected()) {
      if (!messageElement) {
        const errorMessage = document.createElement("div");
        errorMessage.className = "size-error-message";
        errorMessage.style.color = "red";
        errorMessage.style.marginTop = "10px";
        errorMessage.textContent =
          "Please select a size or thickness before adding to cart.";

        // Try to find Size selector first, if not found, fallback to Thickness
        const optionSelector =
          document.querySelector('.option-selector[data-option="Size"]') ||
          document.querySelector('.option-selector[data-option="Thickness"]');

        if (optionSelector) {
          optionSelector.appendChild(errorMessage);
          errorMessage.scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          console.error(
            "No suitable option selector found for Size or Thickness.",
          );
        }
      } else {
        messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else if (messageElement) {
      messageElement.remove();
    }
  }

  // Automatically select the size and thickness if only one is available
  selectSingleOptionIfAvailable(sizeSelectors);
  selectSingleOptionIfAvailable(thicknessSelectors);

  sizeSelectors.forEach((selector) => {
    selector.addEventListener("change", displaySizeErrorMessage);
  });

  thicknessSelectors.forEach((selector) => {
    selector.addEventListener("change", displaySizeErrorMessage);
  });

  // Check size and thickness selection on "Add to Cart" button click
  addToCartButtons.forEach((button) => {
    button.addEventListener("click", function (event) {
      if (!isSizeSelected()) {
        event.preventDefault();
        event.stopImmediatePropagation(); // Prevent modal or form submit
        displaySizeErrorMessage();
        return false;
      }

      // Modal logic: only for sticky ATC with attach modules
      if (
        button.dataset.hasAttachModules === "true" &&
        button.classList.contains("add-to-cart")
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const modal = document.getElementById("at-productModal");
        if (modal) {
          modal.style.display = "block";
          document.body.style.overflow = "hidden";
        }
        return false;
      }
      // ...otherwise, normal ATC flow...
    });
  });

    // Select all elements with the specified selector
  const oosProducts = document.querySelectorAll('a.swatch-product.block.swatch-product-oos');

  // Track if a toast is currently showing
  let isToastShowing = false;

  // Add click event listener to each element
  oosProducts.forEach(product => {
    product.addEventListener('click', (event) => {
      event.preventDefault();

      if (!isToastShowing) {
        isToastShowing = true;
        
        window.csToast?.showToast?.(
          "warning",
          "Sorry, this product is out of stock."
        );

        // Reset flag after toast duration
        setTimeout(() => {
          isToastShowing = false;
        }, 3000);
      }
    });
  });
});
